import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { GridArrowDownwardIcon } from "@mui/x-data-grid";
import { t } from "i18next";
import React, { ChangeEvent, useEffect, useState } from "react";
import Graphs from "./charts/Graphs";
import IntervalTraining from "./charts/IntervalTraining";
import Sectional from "./charts/Sectional";
import { DisplaySettings, Layers } from "@mui/icons-material";
import { scale } from "../../enums/scale.enum";
import { Scale } from "../../pages/trainings/ExamView";
import { TTrainingSection, Theats } from "../../shared/types/training.type";
import {
  convertSpeed,
  getSpeedTooltipForChart,
} from "../../shared/utils/conversion";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import dayjs from "dayjs";
import { getTime } from "../../shared/utils/MapSection";
import { TrainingSetting } from "../../shared/types/trainingSetting.type";
import { useAuth } from "../../hooks/useAuth";
import { isEmpty, lowerCase, toNumber } from "lodash";
import GraphsSectionsSettings from "../MyTrainings/GraphsSectionsSettings";
import AddNewInterval from "../MyTrainings/AddNewInterval";
import Hideable from "../global/Hideable";
import { TPlatformConfiguration } from "../../shared/types/platformConfiguration";
import duration from "dayjs/plugin/duration";
import type { Sectional as TSectional } from "../../shared/types/sectionals.type";
import { TUser } from "../../shared/types/user.type";
import ChukkersAnalysis from "./ChukkersAnalysis";
import { Chukkers } from "../../shared/types/chukkers.type";

dayjs.extend(duration);
type ChartsSectionProps = {
  scaleOption: Scale;
  onChangeScale: (val: Scale) => void;
  distance: string;
  speed: number[];
  bpm: number[];
  rawDistance: number[];
  loCardio: number[];
  time: number[];
  strideFrequency: number[];
  strideLength: number[];
  currentIndex: number;
  flIndex?: number | null;
  loading: boolean;
  isFinishLineActive: boolean;
  heats?: Theats[];
  intervals: TSectional[];
  id?: number;
  editable?: boolean;
  percentageGoodCardio: number;
  onIndexChange: (index: number) => void;
  restrictedMode?: boolean;
  lebelsColor?: string;
  showIntervalTraining?: boolean;
  printable?: boolean;
  userType?: TUser["type"];
  chukkers?: Chukkers;
};

function ChartsSection({
  chukkers,
  userType,
  printable = false,
  showIntervalTraining = false,
  lebelsColor,
  restrictedMode,
  onIndexChange,
  editable,
  id,
  heats,
  loading,
  scaleOption,
  onChangeScale,
  distance,
  speed,
  loCardio,
  bpm,
  rawDistance,
  time,
  strideFrequency,
  strideLength,
  currentIndex,
  flIndex,
  isFinishLineActive,
  intervals,
  percentageGoodCardio,
}: ChartsSectionProps) {
  const metric = useSelector<RootState>(
    (state) => state.user.user?.unit_system,
  ) as "metric" | "imperial" | undefined;
  const userSettings = useSelector<RootState>(
    (state) => state.user?.user?.training_settings,
  ) as TrainingSetting;
  const [hintValue, setHintValue] = useState<string | undefined>("");
  let settingsId = useSelector<RootState>(
    (state) => state.user.user?.training_settings?.id,
  ) as number;
  const { updateUserTraingSetting, settingsUpdating } = useAuth();

  const platformConfigurations = useSelector<RootState>(
    (state) => state.user.platformConfigurations,
  ) as TPlatformConfiguration[];

  const displayAlertBadCardio =
    percentageGoodCardio <
    (toNumber(
      platformConfigurations?.find(
        (c) => c.key === "alert_percentage_good_cardio_treshold_bad",
      )?.value,
    ) ?? 50);

  useEffect(() => {
    if (time.length > 0 && scaleOption && distance) {
      if (scaleOption === "DISTANCE") {
        setHintValue(distance);
      } else {
        let _time = time[time.length - 1] - time[0];
        setHintValue(getTime(_time) + " min");
      }
    }
  }, [scaleOption, time, distance]);

  const onExtremesChanged = (value: number) => {
    if (value === 0) {
      if (scaleOption === "DISTANCE") {
        setHintValue(distance);
      } else {
        const totalSeconds = Math.round(value * 60); // Convert to seconds
        const formattedTime = dayjs
          .duration(totalSeconds, "seconds")
          .format("mm:ss");

        setHintValue(formattedTime + " min");
      }
      return;
    }
    if (scaleOption === "DISTANCE") {
      setHintValue(
        `${value.toFixed(2)} ${metric === "metric" ? " km" : " mi"}`,
      );
    } else {
      const totalSeconds = Math.round(value * 60); // Convert to seconds
      const formattedTime = dayjs
        .duration(totalSeconds, "seconds")
        .format("mm:ss");

      setHintValue(formattedTime + " min");
    }
  };

  const buttonGroup = [
    {
      title: "forthSection:graph",
      value: "GRAPHS",
      enabled: userSettings?.display_section_graphs,
      checkBoxKey: "display_section_graphs",
      isVisible: editable ? true : userSettings?.display_section_graphs,
      component: (
        <>
          <Graphs
            mergedBpmSpeed={userSettings.merge_graphs_speed_bpm}
            mergedStride={
              userSettings.merge_graphs_stride_frequency_stride_length
            }
            printable={printable}
            plotBandId={null}
            setPlotBandId={() => {}}
            labelsColor={lebelsColor}
            hintValue={hintValue}
            onIndexChange={onIndexChange}
            onExtremesChanged={onExtremesChanged}
            flIndex={flIndex}
            isFinishLineActive={isFinishLineActive}
            currentIndex={currentIndex}
            speed={speed}
            bpm={bpm}
            distance={rawDistance}
            scaleOption={scaleOption}
            time={time}
            strideFrequency={strideFrequency}
            strideLength={strideLength}
            loCardio={loCardio}
            displayAlertBadCardio={displayAlertBadCardio}
          ></Graphs>
        </>
      ),
    },
    {
      title: "intervalTrainingAnalysis:title",
      value: "ITA",
      enabled: userSettings?.display_section_interval_training_analysis,
      checkBoxKey: "display_section_interval_training_analysis",
      isVisible:
        showIntervalTraining && editable
          ? true
          : userSettings?.display_section_interval_training_analysis &&
            !isEmpty(heats),
      component: (
        <>
          <IntervalTraining
            printable={printable}
            flIndex={flIndex}
            isFinishLineActive={isFinishLineActive}
            distance={rawDistance}
            time={time}
            heats={heats}
            speed={speed}
            bpm={bpm}
            scaleOption={scaleOption}
          ></IntervalTraining>
        </>
      ),
    },
    {
      title: "intervals:title",
      value: "SECTIONALS",
      enabled: userSettings?.display_section_intervals,
      checkBoxKey: "display_section_intervals",
      isVisible: editable ? true : userSettings?.display_section_intervals,
      component: (
        <>
          <Sectional
            printable={printable}
            restrictedMode={restrictedMode}
            focusOnMainWork={userSettings?.sectionals_focus_main_work}
            isFinishLineActive={isFinishLineActive}
            editable={editable}
            time={time}
            currentIndex={currentIndex}
            intervals={intervals}
            id={id}
          ></Sectional>
        </>
      ),
    },
    {
      title: "chukkers:title",
      value: "CHUKKERS",
      enabled: true,
      checkBoxKey: "display_chukkers",
      isVisible: userType === "POLO",
      component: <ChukkersAnalysis chukkers={chukkers} />,
    },
  ].filter((btn) => btn.isVisible);

  const updateUserSettings = (
    _: ChangeEvent,
    checked: boolean,
    checkboxKey: string,
  ) => {
    updateUserTraingSetting(settingsId, { [checkboxKey]: checked });
  };

  return (
    <>
      {buttonGroup.map((btn, i) => {
        const isLast = i === buttonGroup.length - 1;

        return (
          <Box
            key={btn.value}
            sx={{
              breakInside: "avoid",
              "@media print": {
                breakAfter: isLast ? "auto" : "page",
                pageBreakAfter: isLast ? "auto" : "always", // legacy fallback
              },
            }}
          >
            <Accordion key={btn.value} defaultExpanded>
              <AccordionSummary expandIcon={<GridArrowDownwardIcon />}>
                <Box
                  width={"100%"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Box display={"flex"} alignItems={"center"}>
                    {editable && (
                      <Checkbox
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(_, checked) =>
                          updateUserSettings(_, checked, btn.checkBoxKey)
                        }
                        checked={btn.enabled}
                      ></Checkbox>
                    )}
                    <Typography variant="subtitle2">{t(btn.title)}</Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <AccordionDetails className="slider-container">
                  {loading ? (
                    <Box
                      display={"flex"}
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <CircularProgress
                        size={18}
                        sx={{ mt: 5 }}
                      ></CircularProgress>
                    </Box>
                  ) : (
                    <>
                      {editable && btn.value === "ITA" && (
                        <Alert sx={{ mb: 1 }} severity="info">
                          {t(
                            "trainingSettings:warningIntervalTrainingAnalysis",
                          )}
                        </Alert>
                      )}
                      {!restrictedMode && (
                        <Box
                          display={"flex"}
                          flexDirection={"column"}
                          justifyContent={"space-between"}
                          flexWrap={"wrap"}
                          gap={1}
                        >
                          {/* <Typography variant='body2' sx={{ display: 'flex', gap: 1 }}>{t(scaleOption === 'DISTANCE' ? 'graph:distance' : 'graph:time')} : <Typography variant='body2' fontWeight={500}>{hintValue}</Typography></Typography> */}
                          <Box
                            display={"flex"}
                            justifyContent={"end"}
                            alignItems={"center"}
                            gap={1}
                            flexWrap={"wrap"}
                          >
                            {/* Change What to display on graph Sections */}
                            {editable && (
                              <>
                                {btn.value === "GRAPHS" && (
                                  <Card
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      borderRadius: 3,
                                      p: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color={"#808080"}
                                      display={"flex"}
                                      alignItems={"center"}
                                      gap={"3px"}
                                    >
                                      <DisplaySettings
                                        sx={{ width: 14, height: 14 }}
                                      ></DisplaySettings>
                                      {t("settings:title")}{" "}
                                      {settingsUpdating && (
                                        <CircularProgress
                                          sx={{ ml: 1 }}
                                          size={12}
                                        ></CircularProgress>
                                      )}
                                    </Typography>
                                    <GraphsSectionsSettings
                                      loading={settingsUpdating}
                                      updateSettings={(
                                        key: string,
                                        checked: boolean,
                                      ) =>
                                        updateUserTraingSetting(settingsId, {
                                          [key]: checked,
                                        })
                                      }
                                    ></GraphsSectionsSettings>
                                  </Card>
                                )}
                              </>
                            )}

                            {}

                            {btn.value !== "SECTIONALS" && (
                              <>
                                <Card
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 3,
                                    p: 1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color={"GrayText"}
                                    display={"flex"}
                                    alignItems={"center"}
                                    gap={"3px"}
                                  >
                                    <Layers
                                      sx={{ width: 14, height: 14 }}
                                    ></Layers>
                                    {t(
                                      !editable
                                        ? "graph:scale"
                                        : "settings:defaultGraphUnitType",
                                    )}{" "}
                                    {settingsUpdating && (
                                      <CircularProgress
                                        sx={{ ml: 1 }}
                                        size={12}
                                      ></CircularProgress>
                                    )}
                                  </Typography>
                                  <RadioGroup
                                    row
                                    aria-labelledby="demo-row-radio-buttons-group-label"
                                    name="row-radio-buttons-group"
                                  >
                                    {scale.map((s) => (
                                      <FormControlLabel
                                        key={s.value}
                                        value={s.value}
                                        checked={scaleOption === s.value}
                                        control={
                                          <Radio
                                            onChange={(e) => {
                                              if (editable) {
                                                updateUserTraingSetting(
                                                  settingsId,
                                                  {
                                                    default_graph_unit_type:
                                                      lowerCase(e.target.value),
                                                  },
                                                );
                                              }
                                              onChangeScale(
                                                e.target.value as Scale,
                                              );
                                            }}
                                            size="small"
                                          />
                                        }
                                        label={
                                          <Typography
                                            variant="caption"
                                            fontWeight={500}
                                          >
                                            {t(s.key)}
                                          </Typography>
                                        }
                                      />
                                    ))}
                                  </RadioGroup>
                                </Card>
                              </>
                            )}
                          </Box>
                        </Box>
                      )}

                      {btn.component}
                    </>
                  )}
                </AccordionDetails>
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      })}
    </>
  );
}

export default ChartsSection;
